/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */
import { StateCreator } from 'zustand';
import { produce } from 'immer';
import { createAppStore } from '@marriott/mi-store-utils';

/**
 * Interface for saved property item
 */
export interface SavedPropertyItem {
  id: string;
}

/**
 * Interface for the saved properties data structure
 */
export interface SavedPropertiesData {
  savedPropertiesList: SavedPropertyItem[];
  triggerSaveProperties: boolean;
}

/**
 * Action types that match the original reducer
 */
export type SavedPropertiesActionType = 'added' | 'removed' | 'populateState' | 'populateStateWithCallback';

/**
 * Action interface for dispatch actions
 */
export interface SavedPropertiesAction {
  type: SavedPropertiesActionType;
  payload: string | SavedPropertyItem[];
}

/**
 * Interface for the SavedPropertiesDispatch Context type
 * This matches the original context structure for seamless migration
 */
export interface SavedPropertiesDispatchContextType {
  savedData: SavedPropertiesData;
  dispatch: (action: SavedPropertiesAction) => void;
}

/**
 * Interface for the SavedPropertiesDispatch Zustand store
 * Matches SavedPropertiesDispatchContextType exactly to maintain API compatibility
 */
export interface SavedPropertiesDispatchStore extends SavedPropertiesDispatchContextType {
  // State - matches the context structure
  savedData: SavedPropertiesData;

  // Actions - dispatch function that matches the reducer pattern
  dispatch: (action: SavedPropertiesAction) => void;

  // Individual setter methods for independent store updates
  setSavedData: (savedData: SavedPropertiesData) => void;

  // Store initialization method (following searchFormStore pattern)
  savedPropertiesInitialization: (initialData?: SavedPropertyItem[]) => void;

  // Store management methods
  resetStore: () => void;
}

/**
 * Initial state for the SavedPropertiesDispatch store
 */
const createInitialState = (): Omit<
  SavedPropertiesDispatchStore,
  'dispatch' | 'setSavedData' | 'savedPropertiesInitialization' | 'resetStore'
> => ({
  savedData: {
    savedPropertiesList: [],
    triggerSaveProperties: false,
  },
});

/**
 * SavedPropertiesDispatch Zustand store creator
 * Maintains the same API as the original Context for seamless migration
 */
const SavedPropertiesDispatchStoreCreator: StateCreator<SavedPropertiesDispatchStore> = (set, _get) => {
  return {
    ...createInitialState(),

    // Dispatch function that replicates the original reducer logic
    dispatch: (action: SavedPropertiesAction) => {
      set(
        produce((state: SavedPropertiesDispatchStore) => {
          switch (action.type) {
            case 'added': {
              if (typeof action.payload === 'string') {
                state.savedData.savedPropertiesList.push({ id: action.payload });
                state.savedData.triggerSaveProperties = true;
              }
              break;
            }
            case 'removed': {
              if (typeof action.payload === 'string') {
                state.savedData.savedPropertiesList = state.savedData.savedPropertiesList.filter(
                  (data: SavedPropertyItem) => data.id !== action.payload
                );
                state.savedData.triggerSaveProperties = true;
              }
              break;
            }
            case 'populateState': {
              if (Array.isArray(action.payload)) {
                state.savedData.savedPropertiesList = action.payload;
                state.savedData.triggerSaveProperties = false;
              }
              break;
            }
            case 'populateStateWithCallback': {
              if (Array.isArray(action.payload)) {
                state.savedData.savedPropertiesList = action.payload;
                state.savedData.triggerSaveProperties = true;
              }
              break;
            }
            default: {
              // No action taken for unknown action types
              break;
            }
          }
        })
      );
    },

    // Individual setter methods for independent store updates
    setSavedData: (savedData: SavedPropertiesData) => {
      set(
        produce((state: SavedPropertiesDispatchStore) => {
          state.savedData = {
            savedPropertiesList: [...savedData.savedPropertiesList],
            triggerSaveProperties: savedData.triggerSaveProperties,
          };
        })
      );
    },

    // Store initialization method (following searchFormStore pattern)
    savedPropertiesInitialization: (initialData?: SavedPropertyItem[]) => {
      set(
        produce((state: SavedPropertiesDispatchStore) => {
          if (initialData && Array.isArray(initialData)) {
            state.savedData.savedPropertiesList = [...initialData];
            state.savedData.triggerSaveProperties = false;
          } else {
            // Reset to default values
            const initialState = createInitialState();
            state.savedData = { ...initialState.savedData };
          }
        })
      );
    },

    resetStore: () => {
      set(
        produce((state: SavedPropertiesDispatchStore) => {
          const initialState = createInitialState();
          Object.assign(state, initialState);
        })
      );
    },
  };
};

/**
 * Create the SavedPropertiesDispatch store instance
 * Each component instance should get its own store for component-scoped behavior
 */
export const createSavedPropertiesDispatchStore = () => {
  return createAppStore(SavedPropertiesDispatchStoreCreator, {
    usePersistentStore: false,
  });
};

/**
 * Default store instance - single global instance (no componentId registry)
 */
export const useSavedPropertiesDispatchStore = createSavedPropertiesDispatchStore();
